import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useTpvsService, { getGetTpvQueryKey } from '../../services/TpvsService';
import { useSSE } from '../../layout/SSEProvider';
import { EventMap } from '../../lib/sse/SSEHub';
import { getErrorMessage } from '../../lib/utils';
import {
    DataphoneOperation,
    DataphoneOperationStatuses,
} from '../../types/models/DataphoneOperation';

// Respaldo si se pierde el SSE: el último intento viene en GET /tpvs/:id
export const SIPAY_FALLBACK_POLL_MS = 10_000;
// Tras «aprobado», el TPV pasa a PAID en cuanto se confirma el pago
export const SIPAY_CONFIRMATION_POLL_MS = 2_000;

// Estados del TPV mientras el cobro aprobado aún se está confirmando
const TPV_CONFIRMING_STATUSES = ['CREATING', 'PAYING', 'CONFIRMING'];

// Sin reconciliación, UNKNOWN es un estado final: solo se espera a un SENT
const isWaiting = (operation: DataphoneOperation | null) =>
    operation?.status === DataphoneOperationStatuses.sent;

// El barrido borra el TPV caducado: a partir de ahí no hay nada que consultar
const isNotFound = (error: unknown) =>
    (error as { error?: { status?: number } } | null)?.error?.status === 404;

/**
 * Lanza la venta en el datáfono conectado de Sipay y sigue su resultado: por
 * SSE (`dataphoneOperation:updated`) y, de respaldo, por GET /tpvs/:id.
 * Con el intento aprobado, espera a que el TPV quede PAID para llamar a onPaid.
 */
export default function useSipayDataphoneSale(
    idTpv: number,
    onPaid: () => void,
) {
    const tpvsService = useTpvsService();
    const [launchError, setLaunchError] = useState<string | null>(null);
    const [sseOperation, setSseOperation] = useState<DataphoneOperation | null>(
        null,
    );
    const launched = useRef(false);
    const paidNotified = useRef(false);
    const onPaidRef = useRef(onPaid);
    onPaidRef.current = onPaid;

    const { mutate: launchSale } = tpvsService.useCreatePhysicalTpvPurchase();

    // Un solo lanzamiento por diálogo: PayGate no deduplica y cobraría dos veces
    useEffect(() => {
        if (launched.current) return;
        launched.current = true;
        launchSale(
            { id: idTpv },
            {
                onSuccess: (response) => {
                    const operation = response.sipayPaygate?.operation;
                    if (!operation) {
                        setLaunchError(getErrorMessage(null));
                        return;
                    }
                    setSseOperation((current) => current ?? operation);
                },
                onError: (error) => setLaunchError(getErrorMessage(error)),
            },
        );
    }, [idTpv, launchSale]);

    const onOperationUpdated = useCallback(
        (data: EventMap['dataphoneOperation:updated']) => {
            if (data.tpv.id !== idTpv) return;
            setSseOperation((current) =>
                current && current.id !== data.operation.id
                    ? current
                    : data.operation,
            );
        },
        [idTpv],
    );
    useSSE('dataphoneOperation:updated', onOperationUpdated);

    // El intervalo depende del propio TPV consultado: react-query lo lee al
    // aplicar las opciones, después de este render
    const pollInterval = useRef<number | false>(false);
    const tpvQuery = tpvsService.useGetTpv(idTpv, undefined, {
        queryKey: getGetTpvQueryKey(idTpv),
        enabled: sseOperation !== null,
        refetchInterval: (query) =>
            isNotFound(query.state.error) ? false : pollInterval.current,
        refetchIntervalInBackground: true,
        retry: (failureCount, error) => !isNotFound(error) && failureCount < 3,
    });
    const tpv = tpvQuery.data?.tpv;

    // El TPV solo manda si trae un resultado que el SSE no ha contado
    const operation = useMemo(() => {
        const polled = tpv?.dataphoneOperation;
        if (
            sseOperation &&
            polled &&
            polled.id === sseOperation.id &&
            polled.status !== sseOperation.status &&
            isWaiting(sseOperation)
        ) {
            return polled;
        }
        return sseOperation;
    }, [sseOperation, tpv?.dataphoneOperation]);

    const approved = operation?.status === DataphoneOperationStatuses.approved;
    const tpvStatus = tpv?.status;
    const paid = approved && tpvStatus === 'PAID';
    const notConfirmed =
        approved &&
        tpvStatus !== undefined &&
        tpvStatus !== 'PAID' &&
        !TPV_CONFIRMING_STATUSES.includes(tpvStatus);

    if (isWaiting(operation)) {
        pollInterval.current = SIPAY_FALLBACK_POLL_MS;
    } else if (approved && !paid && !notConfirmed) {
        pollInterval.current = SIPAY_CONFIRMATION_POLL_MS;
    } else {
        pollInterval.current = false;
    }

    // Al aprobarse no se espera al siguiente intervalo para ver si ya está PAID
    const { refetch } = tpvQuery;
    useEffect(() => {
        if (approved) void refetch();
    }, [approved, refetch]);

    useEffect(() => {
        if (paid && !paidNotified.current) {
            paidNotified.current = true;
            onPaidRef.current();
        }
    }, [paid]);

    return { launchError, operation, paid, notConfirmed };
}
